import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/layout/AuthLayout';
import { Button, Input } from '../../components/ui';
import { phoneSchema } from '../../lib/validation';
import { popReturnUrl } from '../../lib/storage';

// OTP input validation
const otpSchema = {
  validate: (value) => {
    if (!value || value.length !== 6) {
      return 'Enter the 6-digit code';
    }
    if (!/^\d{6}$/.test(value)) {
      return 'Code must be 6 digits';
    }
    return true;
  }
};

export default function VerifyPhone() {
  const navigate = useNavigate();
  const {
    isPhoneVerified,
    setupRecaptcha,
    sendOTP,
    verifyOTP,
    cleanupPhoneAuth,
    resetPhone,
    getPhoneState,
    authLoading,
    error: authError,
    clearError,
  } = useAuth();

  const [step, setStep] = useState('phone'); // 'phone', 'otp', or 'recovery'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [phoneState, setPhoneState] = useState(null);

  // Phone form
  const phoneForm = useForm({
    resolver: zodResolver(phoneSchema),
    defaultValues: { phone: '' },
  });

  // OTP form
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');

  // Check phone state on mount and detect inconsistencies
  useEffect(() => {
    const state = getPhoneState();
    setPhoneState(state);
    console.log('[VerifyPhone] Phone state:', state);

    // If Auth has phone but Firestore doesn't, AuthContext will auto-sync
    // Just log it for debugging
    if (state.authHasPhone && !state.firestoreVerified) {
      console.log('[VerifyPhone] Auth has phone but Firestore does not - will auto-sync');
    }
  }, [getPhoneState]);

  // Redirect if already verified
  useEffect(() => {
    if (isPhoneVerified) {
      const returnUrl = popReturnUrl('/');
      navigate(returnUrl);
    }
  }, [isPhoneVerified, navigate]);

  // Initialize reCAPTCHA
  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;

    const initRecaptcha = async () => {
      try {
        console.log('[VerifyPhone] Initializing reCAPTCHA...');
        await setupRecaptcha('recaptcha-container', () => {
          if (mounted) {
            console.log('[VerifyPhone] reCAPTCHA ready');
            setRecaptchaReady(true);
          }
        });
      } catch (err) {
        console.error('[VerifyPhone] reCAPTCHA init error:', err);

        // Retry on failure (network issues, etc.)
        if (mounted && retryCount < maxRetries) {
          retryCount++;
          console.log(`[VerifyPhone] Retrying reCAPTCHA init (${retryCount}/${maxRetries})...`);
          setTimeout(initRecaptcha, 1000 * retryCount); // Exponential backoff
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initRecaptcha, 100);

    return () => {
      mounted = false;
      clearTimeout(timer);
      cleanupPhoneAuth();
    };
  }, [setupRecaptcha, cleanupPhoneAuth]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSendOTP = async (data) => {
    try {
      clearError();
      await sendOTP(data.phone);
      setPhoneNumber(data.phone);
      setStep('otp');
      setResendCooldown(60);
    } catch (err) {
      // Error handled by AuthContext
    }
  };

  const handleVerifyOTP = async () => {
    const validation = otpSchema.validate(otp);
    if (validation !== true) {
      setOtpError(validation);
      return;
    }

    setOtpError('');

    try {
      clearError();
      await verifyOTP(otp);
      // Will redirect via useEffect when isPhoneVerified changes
    } catch (err) {
      // Error handled by AuthContext
    }
  };

  const handleResendOTP = async () => {
    try {
      clearError();
      await sendOTP(phoneNumber);
      setResendCooldown(60);
    } catch (err) {
      // Error handled by AuthContext
    }
  };

  const handleChangeNumber = () => {
    setStep('phone');
    setOtp('');
    setOtpError('');
    clearError();
  };

  const handleResetPhone = async () => {
    try {
      clearError();
      await resetPhone();
      // Refresh phone state
      setPhoneState(getPhoneState());
      setStep('phone');
    } catch (err) {
      // Error handled by AuthContext
    }
  };

  // Format phone number as user types
  const formatPhoneDisplay = (phone) => {
    // Show formatted for display
    if (phone.startsWith('+353')) {
      const local = phone.slice(4);
      return `+353 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5)}`.trim();
    }
    return phone;
  };

  return (
    <AuthLayout
      title={step === 'phone' ? 'Verify your phone' : 'Enter verification code'}
      subtitle={
        step === 'phone'
          ? 'We need your phone number for security and to notify you about competitions.'
          : `We sent a code to ${formatPhoneDisplay(phoneNumber)}`
      }
    >
      {/* reCAPTCHA container - must be in DOM */}
      <div id="recaptcha-container" className="mb-4" />

      {/* Error */}
      {authError && (
        <div className="rounded-md bg-danger/10 border border-danger/20 p-4 mb-6">
          <p className="text-sm text-danger">{authError}</p>
          {/* Show reset option for "already linked" errors */}
          {(authError.includes('already linked') || authError.includes('already-in-use')) && (
            <button
              type="button"
              onClick={handleResetPhone}
              disabled={authLoading}
              className="mt-3 text-sm text-primary hover:text-primary-hover underline"
            >
              Reset and try with a different number
            </button>
          )}
        </div>
      )}

      {/* Phone state debug info (only in development) */}
      {import.meta.env.DEV && phoneState && (
        <details className="mb-4 text-xs text-midnight/50">
          <summary className="cursor-pointer">Debug: Phone state</summary>
          <pre className="mt-2 p-2 bg-midnight-lighter rounded text-cream/60 overflow-auto">
{JSON.stringify(phoneState, null, 2)}
          </pre>
        </details>
      )}

      {step === 'phone' ? (
        // Phone number input step
        <form onSubmit={phoneForm.handleSubmit(handleSendOTP)} className="space-y-6">
          <div>
            <Input
              label="Phone number"
              type="tel"
              autoComplete="tel"
              placeholder="+353 87 123 4567"
              helperText="Irish mobile numbers only (+353)"
              error={phoneForm.formState.errors.phone?.message}
              dark
              {...phoneForm.register('phone')}
            />
          </div>

          <Button
            type="submit"
            fullWidth
            loading={authLoading}
            disabled={!recaptchaReady}
          >
            {!recaptchaReady ? 'Loading...' : 'Send verification code'}
          </Button>

          <p className="text-xs text-gray-500 text-center">
            Standard SMS rates may apply.
          </p>
        </form>
      ) : (
        // OTP verification step
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verification code
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setOtp(value);
                if (otpError) setOtpError('');
              }}
              className={`
                block w-full text-center text-2xl tracking-widest font-mono
                rounded-md border px-4 py-3 bg-white text-midnight
                placeholder:text-midnight/40
                focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
                ${otpError ? 'border-error' : 'border-gray-300'}
              `}
              placeholder="000000"
            />
            {otpError && (
              <p className="mt-2 text-sm text-error">{otpError}</p>
            )}
          </div>

          <Button
            onClick={handleVerifyOTP}
            fullWidth
            loading={authLoading}
          >
            Verify
          </Button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={handleChangeNumber}
              className="text-primary hover:text-primary-hover"
            >
              Change number
            </button>

            <button
              type="button"
              onClick={handleResendOTP}
              disabled={resendCooldown > 0 || authLoading}
              className={`
                ${resendCooldown > 0 || authLoading
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-primary hover:text-primary-hover'
                }
              `}
            >
              {resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : 'Resend code'}
            </button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
